import {
    SimplePageContentWrapper,
    SimplePageHeader,
    SimplePageLayout,
} from '@flexinet/ui-components';
import {
    Alert,
    Badge,
    Group,
    Table,
    Text,
} from '@mantine/core';
import {AiOutlineInfoCircle} from 'react-icons/ai';

import {useRegisteredActionsQuery} from '../../testing-api-integration/hooks/useRegisteredActions';

// FNP-995: the handler registry, on a page.
//
// The owner's request was blunt — "you keep saying 'the registry'; where the
// hell is it?" This is the answer: what the deployed canary bundle can actually
// PERFORM, as opposed to what the Action catalog claims.
//
// ⚠️ READ-ONLY BY CONSTRUCTION, not by permission. There is no mutation behind
// any of this: a handler declares its own actionCode about itself, and the only
// way to change this list is to ship a handler. So there is no add button, no
// row action, and no edit route — and `dependsOn` in particular is code-owned,
// because run-wake.ts interprets it to drive skip-vs-attempt and cascade
// attribution.

export const IndexPage = () => {

    const {
        data,
        isLoading,
        isError,
    } = useRegisteredActionsQuery();

    const actions = data ?? [];

    return (
        <SimplePageLayout
            pageHeader={
                <SimplePageHeader
                    title={'Handler Registry'}
                />
            }
        >
            <SimplePageContentWrapper>

                <Alert
                    variant={'light'}
                    color={'blue'}
                    icon={<AiOutlineInfoCircle/>}
                    mb={'lg'}
                >
                    These are the behaviours the deployed executor knows how to
                    carry out. An Action in the catalog names one of these codes;
                    a code with no handler here is still legal and records an
                    &quot;unimplemented&quot; observation until a handler ships.
                    This list comes from code and cannot be edited.
                </Alert>

                {isError && (
                    <Alert variant={'light'} color={'red'} icon={<AiOutlineInfoCircle/>}>
                        The registry could not be read.
                    </Alert>
                )}

                {!isError && !isLoading && actions.length === 0 && (
                    <Alert variant={'light'} color={'gray'} icon={<AiOutlineInfoCircle/>}>
                        No handlers are registered in this deployment.
                    </Alert>
                )}

                {actions.length > 0 && (
                    <Table striped highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Action Code</Table.Th>
                                <Table.Th>Depends On</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {actions.map(action => (
                                <Table.Tr key={action.actionCode}>
                                    <Table.Td>
                                        <Text ff={'monospace'}>{action.actionCode}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                        {action.dependsOn.length === 0 ? (
                                            <Text c={'dimmed'} size={'sm'}>none</Text>
                                        ) : (
                                            <Group gap={'xs'}>
                                                {action.dependsOn.map(dependency => (
                                                    <Badge
                                                        key={dependency}
                                                        variant={'light'}
                                                        color={'gray'}
                                                    >
                                                        {dependency}
                                                    </Badge>
                                                ))}
                                            </Group>
                                        )}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}

            </SimplePageContentWrapper>
        </SimplePageLayout>
    );
};
